import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { 
  TrendingUp, 
  Euro, 
  BarChart3,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus,
  Download,
  Star
} from 'lucide-react';
import { getDashboardOverview, getProductEvolution, getPortfolioVsMarket, ProductDistribution, RecentInvestment, InvestedByProduct, ProductPerformance, PortfolioVsMarketProduct } from '../services/investmentService';
import api from '../services/api';
import { getDeviationBadgeClass, formatDeviation, getGrowthColorClass } from '../utils/deviationColors';
import PerformanceChart from '../components/PerformanceChart';
import AreaDistributionChart from '../components/AreaDistributionChart';
import ProductEvolutionChart from '../components/ProductEvolutionChart';
import PortfolioVsMarketChart from '../components/PortfolioVsMarketChart';
import PlatformCards from '../components/PlatformCards';
import PortfolioInsights from '../components/PortfolioInsights';
import SensitiveValue from '../components/SensitiveValue';
import { usePublicData } from '../hooks/usePublicData';
import { usePublicApi } from '../hooks/usePublicApi';

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data) as T[];
  return [];
}

const Dashboard: React.FC = () => {
  const location = useLocation();
  const [refreshKey, setRefreshKey] = React.useState(Date.now());
  const [isExporting, setIsExporting] = React.useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = React.useState(false);
  
  // Verificar se estamos no contexto público
  const { data: publicData, isLoading: publicLoading, error: publicError, isPublic } = usePublicData();
  const publicApi = usePublicApi();
  const staticQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 10,
  };
  
  const { data: dashboardData, isLoading, error } = useQuery(
    ['dashboard-overview', refreshKey],
    getDashboardOverview,
    {
      enabled: !isPublic, // Só fazer query se não estiver no contexto público
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // Usar dados públicos se estivermos no contexto público, senão usar dados normais
  const finalDashboardData = isPublic ? publicData : dashboardData;
  const finalIsLoading = isPublic ? publicLoading : isLoading;
  const finalError = isPublic ? publicError : error;

  // 1. PLATFORM DISTRIBUTION - Carrega primeiro (aparece visualmente em 2º lugar)
  const { data: platformDistributionData, isLoading: isLoadingPlatformDistribution } = useQuery(
    ['platform-distribution', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/platform-distribution').then(res => res.data);
      }
      return api.get('/dashboard/platform-distribution').then(res => res.data);
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // 2. PRODUCT PERFORMANCE - Carrega cedo (usado nos Insights que aparecem em 4º lugar)
  const { data: productPerformanceData, isLoading: isLoadingProductPerformance } = useQuery<ProductPerformance[]>(
    ['product-performance', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/product-performance').then(res => res.data);
      }
      return api.get('/dashboard/product-performance').then(res => res.data);
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // 3. PRODUCT DISTRIBUTION - Carrega antes da tabela (aparece em 5º lugar)
  const { data: productDistributionData, isLoading: isLoadingProductDistribution } = useQuery<ProductDistribution[]>(
    ['product-distribution', refreshKey],
    async () => {
      const response = isPublic && publicApi
        ? await publicApi.get('/dashboard/product-distribution')
        : await api.get('/dashboard/product-distribution');
      return asArray<ProductDistribution>(response.data);
    },
    {
      enabled: isPublic ? !!publicApi : true,
      refetchInterval: 30000,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  // 4. PRODUCT EVOLUTION - Carrega antes do gráfico (aparece em 7º lugar)
  const { data: productEvolutionData, isLoading: isLoadingProductEvolution } = useQuery(
    ['product-evolution', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/product-evolution').then(res => res.data);
      }
      return getProductEvolution();
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  const { data: portfolioVsMarketData, isLoading: isLoadingPortfolioVsMarket } = useQuery<PortfolioVsMarketProduct[]>(
    ['portfolio-vs-market', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/portfolio-vs-market').then(res => res.data);
      }
      return getPortfolioVsMarket();
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // 5. RECENT INVESTMENTS - Carrega antes da tabela (aparece em 8º lugar)
  const { data: recentInvestmentsData, isLoading: isLoadingRecentInvestments } = useQuery<RecentInvestment[]>(
    ['recent-investments', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/recent-investments').then(res => res.data);
      }
      return api.get('/dashboard/recent-investments').then(res => res.data);
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // 6. INVESTED BY PRODUCT - Carrega por último (aparece em 9º lugar)
  const { data: investedByProductData, isLoading: isLoadingInvestedByProduct } = useQuery<InvestedByProduct[]>(
    ['invested-by-product', refreshKey],
    () => {
      if (isPublic && publicApi) {
        return publicApi.get('/dashboard/invested-by-product').then(res => res.data);
      }
      return api.get('/dashboard/invested-by-product').then(res => res.data);
    },
    {
      enabled: isPublic ? !!publicApi : true,
      keepPreviousData: true,
      ...staticQueryOptions,
    }
  );

  // Force refresh when navigating to dashboard
  useEffect(() => {
    if (location.pathname === '/') {
      console.log('Dashboard: Forcing refresh on navigation');
      setRefreshKey(Date.now());
    }
  }, [location.pathname]);

  const handleRefresh = async () => {
    console.log('Dashboard: Manual refresh triggered');
    setIsManualRefreshing(true);
    if (!isPublic) {
      try {
        await api.get('/dashboard/clear-cache');
      } catch (cacheError) {
        console.error('Dashboard: Erro ao limpar cache antes do refresh', cacheError);
      }
    }
    setRefreshKey(Date.now());
  };

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      console.log('Dashboard: Exporting to HTML...');
      
      // Fazer download do HTML
      const response = await api.get('/dashboard/export-pdf', {
        responseType: 'blob',
        headers: {
          'Accept': 'text/html'
        }
      });
      
      // Criar URL do blob e fazer download
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Dashboard: HTML exported successfully');
    } catch (error) {
      console.error('Erro ao exportar HTML:', error);
      alert('Erro ao exportar relatório. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  const isAnySectionLoading =
    finalIsLoading ||
    isLoadingProductEvolution ||
    isLoadingPortfolioVsMarket ||
    isLoadingProductDistribution ||
    isLoadingPlatformDistribution ||
    isLoadingRecentInvestments ||
    isLoadingInvestedByProduct ||
    isLoadingProductPerformance;

  useEffect(() => {
    if (!isAnySectionLoading) {
      setIsManualRefreshing(false);
    }
  }, [isAnySectionLoading]);

  if (finalError) {
    return (
      <div className="card">
        <div className="flex items-center text-danger-600">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>Erro ao carregar dados do dashboard</span>
        </div>
      </div>
    );
  }

  if (!finalDashboardData && !finalIsLoading) {
    return (
      <div className="card">
        <div className="text-center text-gray-500">
          Nenhum dado disponível
        </div>
      </div>
    );
  }

  const portfolioFallback: any = {
    total_value_eur: 0,
    distribution_data: null,
    growth_data: null,
    investment_data: {
      total_current_investment_eur: 0,
      total_investment_change_eur: 0,
      total_investment_change_percentage: 0,
    },
    drawdown: null,
    annualized_return: null,
    last_capital_increase: null,
    last_investment: null,
  };

  const portfolio = finalDashboardData?.portfolio ?? portfolioFallback;
  const evolution = finalDashboardData?.evolution ?? [];
  const area_distribution = finalDashboardData?.area_distribution;

  const formatCurrency = (amount: number | string | null | undefined) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || 0));
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(numAmount);
  };

  const formatPercentage = (value: number | string | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
    return `${numValue.toFixed(2)}%`;
  };

  const getNegativeIsRedColorClass = (value: number | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
    if (numValue < 0) return 'text-danger-600';
    if (numValue > 0) return 'text-success-600';
    return 'text-gray-600';
  };

  // Função para obter a cor da área baseada no índice
  const getAreaColor = (index: number) => {
    const colors = [
      'rgb(59, 130, 246)',   // Blue
      'rgb(16, 185, 129)',   // Green
      'rgb(245, 158, 11)',   // Yellow
      'rgb(239, 68, 68)',    // Red
      'rgb(139, 92, 246)',   // Purple
      'rgb(236, 72, 153)',   // Pink
    ];
    return colors[index % colors.length];
  };

  // Função para renderizar indicador de variação
  const renderVariationIndicator = (variationDirection: string, variationPercent: number) => {
    const getVariationColor = (direction: string) => {
      switch (direction) {
        case 'up': return 'text-green-600';
        case 'down': return 'text-red-600';
        default: return 'text-gray-500';
      }
    };

    const getVariationIcon = (direction: string) => {
      switch (direction) {
        case 'up': return <ArrowUp className="h-3 w-3" />;
        case 'down': return <ArrowDown className="h-3 w-3" />;
        default: return <Minus className="h-3 w-3" />;
      }
    };

    return (
      <div className={`flex items-center ${getVariationColor(variationDirection)}`}>
        {getVariationIcon(variationDirection)}
        <span className="ml-1 text-xs font-medium">
          {Math.abs(variationPercent).toFixed(1)}%
        </span>
      </div>
    );
  };

  const renderSectionLoading = (title: string) => (
    <div className="card">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Visão geral do seu portefólio de investimentos</p>
        </div>
        <div className="flex gap-3">
          {/* Só mostrar botão de exportar se não estiver no contexto público */}
          {!isPublic && (
            <button
              onClick={handleExportPdf}
              disabled={isExporting}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  A gerar...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Relatório
                </>
              )}
            </button>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isManualRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Euro className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Valor Total</p>
              <p className="text-2xl font-semibold text-gray-900">
                <SensitiveValue>
                  {formatCurrency(portfolio.total_value_eur)}
                </SensitiveValue>
              </p>
              {portfolio.growth_data && (
                <p className={`text-sm ${getGrowthColorClass(portfolio.growth_data.total_growth_eur)}`}>
                  <SensitiveValue>
                    {portfolio.growth_data.total_growth_eur >= 0 ? '+' : ''}{formatCurrency(portfolio.growth_data.total_growth_eur)}
                  </SensitiveValue>
                  {' '}
                  ({portfolio.growth_data.total_growth_percentage >= 0 ? '+' : ''}{formatPercentage(portfolio.growth_data.total_growth_percentage)})
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Investimentos</p>
              <p className="text-2xl font-semibold text-gray-900">
                <SensitiveValue>
                  {formatCurrency(portfolio.investment_data.total_current_investment_eur)}
                </SensitiveValue>
              </p>
              {portfolio.investment_data && (
                <p className={`text-sm ${getGrowthColorClass(portfolio.investment_data.total_investment_change_eur)}`}>
                  <SensitiveValue>
                    {portfolio.investment_data.total_investment_change_eur >= 0 ? '+' : ''}{formatCurrency(portfolio.investment_data.total_investment_change_eur)}
                  </SensitiveValue>
                  {' '}
                  ({portfolio.investment_data.total_investment_change_percentage >= 0 ? '+' : ''}{formatPercentage(portfolio.investment_data.total_investment_change_percentage)})
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-8 w-8 text-warning-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Variação Portefólio</p>
              <p className={`text-xl font-semibold ${getGrowthColorClass(portfolio.drawdown?.max_growth_percentage ?? 0)}`}>
                Max: {formatPercentage(portfolio.drawdown?.max_growth_percentage ?? 0)}
              </p>
              <p className={`text-xl font-semibold ${getNegativeIsRedColorClass(portfolio.drawdown?.min_growth_percentage ?? 0)}`}>
                Min: {formatPercentage(portfolio.drawdown?.min_growth_percentage ?? 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Rentabilidade Anualizada</p>
              <p className={`text-2xl font-semibold ${getGrowthColorClass(portfolio.annualized_return?.annualized_return_percentage ?? 0)}`}>
                {formatPercentage(portfolio.annualized_return?.annualized_return_percentage ?? 0)}
              </p>
              {!portfolio.annualized_return?.has_data && (
                <p className="text-sm text-gray-500">Sem histórico suficiente</p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-4 flex-1">
              <div className="mb-2">
                <p className="text-xs font-medium text-gray-500">Última Adição de Fundos</p>
                <p className="text-sm font-semibold text-gray-900">
                  {portfolio.last_capital_increase 
                    ? new Date(portfolio.last_capital_increase.increase_date).toLocaleDateString('pt-PT')
                    : 'N/A'
                  }
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Última Leitura</p>
                <p className="text-sm font-semibold text-gray-900">
                  {portfolio.last_investment 
                    ? new Date(portfolio.last_investment.insertion_date).toLocaleDateString('pt-PT')
                    : 'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Distribution */}
      {isLoadingPlatformDistribution ? renderSectionLoading('Distribuição por Plataforma') : platformDistributionData && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição por Plataforma</h3>
          <PlatformCards data={platformDistributionData} />
        </div>
      )}

      {/* Area Distribution */}
      {area_distribution && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição por Área</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <AreaDistributionChart data={area_distribution} />
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Área</th>
                    <th>Valor</th>
                    <th>% Atual</th>
                    <th>% Alvo</th>
                    <th>Desvio</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Ordenar por valor (como estava originalmente)
                    const sortedAreas = area_distribution.sort((a: any, b: any) => b.current_percentage - a.current_percentage);
                    
                    // Calcular melhor e pior performance baseado no crescimento
                    const areasWithGrowth = area_distribution.filter((area: any) => area.variation_percent !== undefined);
                    const sortedByGrowth = areasWithGrowth.sort((a: any, b: any) => (b.variation_percent || 0) - (a.variation_percent || 0));
                    const bestGrowthArea = sortedByGrowth.length > 0 ? sortedByGrowth[0] : null;
                    const worstGrowthArea = sortedByGrowth.length > 0 ? sortedByGrowth[sortedByGrowth.length - 1] : null;
                    
                    return sortedAreas.map((area: any, index: number) => {
                      const isBestArea = bestGrowthArea && area.area_name === bestGrowthArea.area_name;
                      const isWorstArea = worstGrowthArea && area.area_name === worstGrowthArea.area_name;
                      
                      return (
                    <tr key={area.area_name}>
                      <td className="font-medium">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: getAreaColor(index) }}
                            ></div>
                            <span>{area.area_name}</span>
                            {isBestArea && (
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 ml-2" />
                            )}
                            {isWorstArea && (
                              <AlertCircle className="h-4 w-4 text-red-500 ml-2" />
                            )}
                          </div>
                          {area.variation_direction && (
                            <div className="ml-2">
                              {renderVariationIndicator(
                                area.variation_direction, 
                                area.variation_percent || 0
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <SensitiveValue>
                          {formatCurrency(area.current_value_eur)}
                        </SensitiveValue>
                      </td>
                      <td>{formatPercentage(area.current_percentage)}</td>
                      <td>{formatPercentage(area.target_percentage)}</td>
                      <td>
                        <span className={`badge ${getDeviationBadgeClass(area.deviation)}`}>
                          {formatDeviation(area.deviation)}
                        </span>
                      </td>
                    </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Insights */}
      {(isLoadingProductPerformance || isLoadingProductEvolution) ? renderSectionLoading('Insights do Portefólio') : portfolio.distribution_data && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Insights do Portefólio</h3>
          <PortfolioInsights 
            distributionData={portfolio.distribution_data} 
            performanceData={productPerformanceData || []}
            productEvolutionData={productEvolutionData || []}
          />
        </div>
      )}

      {/* Product Distribution */}
      {isLoadingProductDistribution ? renderSectionLoading('Distribuição por Produto') : Array.isArray(productDistributionData) && productDistributionData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição por Produto</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Área</th>
                  <th>Produto</th>
                  <th>Valor</th>
                  <th>Lucro (€)</th>
                  <th>Rentabilidade (%)</th>
                  <th>% Atual</th>
                  <th>% Alvo</th>
                  <th>Desvio</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Agrupar dados por área
                  const groupedByArea = productDistributionData.reduce((acc, product) => {
                    if (!acc[product.area_name]) {
                      acc[product.area_name] = [];
                    }
                    acc[product.area_name].push(product);
                    return acc;
                  }, {} as Record<string, typeof productDistributionData>);

                  // Identificar melhor e pior desempenho baseado nos dados de performance
                  const bestPerformerId = productPerformanceData && productPerformanceData.length > 0 ? productPerformanceData[0].product_id : null;
                  const worstPerformerId = productPerformanceData && productPerformanceData.length > 0 ? productPerformanceData[productPerformanceData.length - 1].product_id : null;

                  // Ordenar áreas por peso (valor total) - maior primeiro
                  const sortedAreas = Object.keys(groupedByArea).sort((a, b) => {
                    const areaAValue = groupedByArea[a].reduce((sum, p) => sum + p.current_value_eur, 0);
                    const areaBValue = groupedByArea[b].reduce((sum, p) => sum + p.current_value_eur, 0);
                    return areaBValue - areaAValue;
                  });

                  const rows: JSX.Element[] = [];

                  sortedAreas.forEach((areaName) => {
                    const areaProducts = groupedByArea[areaName];
                    
                    // Ordenar produtos dentro da área alfabeticamente
                    const sortedProducts = areaProducts.sort((a, b) => 
                      a.product_name.localeCompare(b.product_name, 'pt-PT')
                    );

                    // Calcular totais da área
                    const areaTotalValue = areaProducts.reduce((sum, p) => sum + p.current_value_eur, 0);
                    const areaTotalTarget = areaProducts.reduce((sum, p) => sum + p.target_percentage, 0);
                    const areaTotalCurrent = areaProducts.reduce((sum, p) => sum + p.current_percentage, 0);
                    const areaDeviation = areaTotalCurrent - areaTotalTarget;

                    // Adicionar cabeçalho da área
                    rows.push(
                      <tr key={`area-${areaName}`} className="bg-gray-50 border-t-2 border-gray-200">
                        <td colSpan={8} className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="font-bold text-gray-900 text-lg">{areaName}</span>
                              <span className="ml-2 text-sm text-gray-600">
                                (<SensitiveValue>{formatCurrency(areaTotalValue)}</SensitiveValue>)
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-sm text-gray-600 mr-2">
                                Total: {formatPercentage(areaTotalCurrent)} / {formatPercentage(areaTotalTarget)}
                              </span>
                              <span className={`badge ${getDeviationBadgeClass(areaDeviation)}`}>
                                {formatDeviation(areaDeviation)}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );

                    // Adicionar produtos da área
                    sortedProducts.forEach((product) => {
                      // Calcular percentagem dentro da área
                      const areaCurrentPercentage = areaTotalValue > 0 ? (product.current_value_eur / areaTotalValue) * 100 : 0;
                      const areaTargetPercentage = areaTotalTarget > 0 ? (product.target_percentage / areaTotalTarget) * 100 : 0;
                      const areaProductDeviation = areaCurrentPercentage - areaTargetPercentage;

                      const isBestPerformer = product.product_id === bestPerformerId;
                      const isWorstPerformer = product.product_id === worstPerformerId;

                      rows.push(
                        <tr key={product.product_id} className="border-l-4 border-gray-200">
                          <td className="text-gray-500 pl-4">{product.area_name}</td>
                          <td className="font-medium">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <span>{product.product_name}</span>
                                {isBestPerformer && (
                                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 ml-2" />
                                )}
                                {isWorstPerformer && (
                                  <AlertCircle className="h-4 w-4 text-red-500 ml-2" />
                                )}
                              </div>
                              {product.variation_direction && (
                                <div className="ml-2">
                                  {renderVariationIndicator(
                                    product.variation_direction, 
                                    product.variation_percent || 0
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <SensitiveValue>
                              {formatCurrency(product.current_value_eur)}
                            </SensitiveValue>
                          </td>
                          <td className={getGrowthColorClass(product.profit_eur ?? 0)}>
                            <SensitiveValue>
                              {product.profit_eur !== undefined
                                ? `${(product.profit_eur ?? 0) >= 0 ? '+' : ''}${formatCurrency(product.profit_eur ?? 0)}`
                                : '-'}
                            </SensitiveValue>
                          </td>
                          <td className={getGrowthColorClass(product.return_percentage ?? 0)}>
                            {product.return_percentage !== undefined
                              ? `${(product.return_percentage ?? 0) >= 0 ? '+' : ''}${formatPercentage(product.return_percentage ?? 0)}`
                              : '-'}
                          </td>
                          <td>{formatPercentage(areaCurrentPercentage)}</td>
                          <td>{formatPercentage(areaTargetPercentage)}</td>
                          <td>
                            <span className={`badge ${getDeviationBadgeClass(areaProductDeviation)}`}>
                              {formatDeviation(areaProductDeviation)}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  });

                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}




      {/* Portfolio Evolution */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Evolução do Portefólio</h3>
        <PerformanceChart data={evolution} />
      </div>

      {/* Growth Analysis */}
      {isLoadingProductEvolution ? renderSectionLoading('Variação por Produto') : productEvolutionData && productEvolutionData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Variação por Produto</h3>
          <ProductEvolutionChart data={productEvolutionData} />
        </div>
      )}

      {isLoadingPortfolioVsMarket ? renderSectionLoading('Portefólio vs Mercado') : portfolioVsMarketData && portfolioVsMarketData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Portefólio vs Mercado</h3>
          <PortfolioVsMarketChart data={portfolioVsMarketData} />
        </div>
      )}


      {/* Recent Investments */}
      {isLoadingRecentInvestments ? renderSectionLoading('Últimos 5 Investimentos') : recentInvestmentsData && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Últimos 5 Investimentos</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Valor Total</th>
                  <th>Moeda</th>
                  <th>Produtos Distribuídos</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {recentInvestmentsData.map((investment) => (
                  <tr key={investment.id}>
                    <td className="font-medium">{investment.formatted_date}</td>
                    <td>
                      <SensitiveValue>
                        {formatCurrency(investment.total_amount)}
                      </SensitiveValue>
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {investment.base_currency_code}
                      </span>
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {investment.distributed_products} {investment.distributed_products === 1 ? 'produto' : 'produtos'}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">
                      {investment.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invested by Product */}
      {isLoadingInvestedByProduct ? renderSectionLoading('Total Investido por Produto') : investedByProductData && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Total Investido por Produto</h3>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Área</th>
                  <th>Produto</th>
                  <th>Total Investido</th>
                  <th>Moeda</th>
                  <th>Nº Investimentos</th>
                  <th>Primeiro</th>
                  <th>Último</th>
                </tr>
              </thead>
              <tbody>
                {investedByProductData.map((product) => (
                  <tr key={product.product_id}>
                    <td className="font-medium text-sm">{product.area_name}</td>
                    <td>{product.product_name}</td>
                    <td className="font-semibold text-primary-600">
                      <SensitiveValue>
                        {formatCurrency(product.total_invested_eur)}
                      </SensitiveValue>
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {product.product_currency_code}
                      </span>
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {product.number_of_investments}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">{product.formatted_first_date}</td>
                    <td className="text-sm text-gray-500">{product.formatted_last_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
